/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {createListenerRegistry} from '@ion/core/src/utils/ListenerRegistry';
import {Notification} from '@ion/dls/src/types/extensions';
import {JobStatusUpdate} from '@ion/services/src/websocket/types';
import {
  getNotifications,
  getNotifiedJobIds,
  JobNotificationEntry,
  update
} from './WatchedJobs';

export type JobNotificationListener = (entry: JobNotificationEntry) => void;

const listeners = createListenerRegistry<[JobNotificationEntry]>();

/**
 * Builds one panel row from one terminal event.
 *
 * **Nothing in the payload is guaranteed.** `JobStatusUpdate` declares `name`,
 * `lastModifiedOn` and `progress` all optional, because an unwrapped change
 * carries only the attributes the message itself carried - so every field here
 * has a fallback rather than an assumption:
 *
 * - absent `name` falls back to the job id, so nothing renders `undefined`
 * - absent `lastModifiedOn` falls back to the moment of discovery. This is the
 *   one case where `occurredAt` is *not* the job's own timestamp, and it exists
 *   because the panel groups rows by date: an absent value produces an Invalid
 *   Date group header
 * - absent `progress` leaves the status empty, so the card's secondary line is
 *   empty rather than broken
 */
const toEntry = (
  jobId: number,
  {name, lastModifiedOn, progress}: JobStatusUpdate,
  discoveredAt: string
): JobNotificationEntry => ({
  jobId,
  name       : name ?? String(jobId),
  status     : progress?.status ?? '',
  reason     : progress?.reason ?? undefined,
  occurredAt : lastModifiedOn ?? discoveredAt,
  hasSeen    : false
});

/**
 * Creates one notification for one terminal job, deduped against the permanent
 * record of everything we have ever notified about.
 *
 * The one creation path, shared by the socket and the catch-up query. There is
 * no terminal-status list to evaluate on the socket path: the service emits
 * `jobStatus` once, at terminal, so receiving it *is* the terminal event.
 *
 *  **The entry and its tombstone land in one write, with no `await` between
 * the two mutations.** If the notification write were lost while the tombstone
 * survived, the catch-up query would skip that job *forever* and the user would
 * never learn it finished. Both live in the same preference key and are mutated
 * in one synchronous block, so they cannot be separated - which is why the test
 * for this counts saves rather than `set` calls.
 *
 * Listeners are notified as soon as the in-memory lists carry the entry, so the
 * badge does not wait on the POST.
 *
 * @param   jobId The job which finished.
 * @param   status The terminal change, from either channel.
 * @param   discoveredAt Fallback timestamp for a payload carrying none.
 * @returns `true` when a notification was created and the write confirmed.
 */
export const createFromStatus = (
  jobId: number,
  status: JobStatusUpdate,
  discoveredAt: string = new Date().toISOString()
): Promise<boolean> => {
  const id = Number(jobId);
  let written = Promise.resolve(false);

  if (!getNotifiedJobIds()?.includes(id)) {
    const entry = toEntry(id, status ?? {}, discoveredAt);

    written = update(current => {
      current.notifications  = current.notifications.concat(entry);
      current.notifiedJobIds = current.notifiedJobIds.concat(id);
    });
    listeners.notify(entry);
  }
  return written;
};

/**
 * Flips every stored notification to seen, and writes once.
 *
 * Called when the panel opens, which clears the badge for **every** entry
 * including the rows the user never scrolled to. That is intended, and it means
 * an accidental open clears the badge for good.
 *
 * Nothing is written when there was nothing unseen, so opening the panel
 * repeatedly costs one write rather than one per open.
 *
 * @returns `true` when the write was confirmed.
 */
export const markPanelSeen = (): Promise<boolean> => {
  let written = Promise.resolve(false);

  if (getNotifications()?.some(entry => !entry?.hasSeen)) {
    written = update(current => {
      current.notifications = current.notifications.map(entry => ({...entry, hasSeen: true}));
    });
  }
  return written;
};

/**
 * Drops cleared notifications from the store, so a delete survives a reload.
 *
 *  **The tombstones stay.** A deleted notification must never be recreated,
 * and the catch-up query is what would recreate it: the job is still watched
 * and still terminal, so only its presence in `notifiedJobIds` stops it coming
 * back at the next sign-in.
 *
 * @param   cleared The notifications the panel just cleared, identified by the
 *          job id the adapter put in `id`.
 * @returns `true` when the write was confirmed.
 */
export const deleteNotifications = (cleared: Notification[]): Promise<boolean> => {
  const ids = (cleared ?? []).map(({id}) => Number(id));
  let written = Promise.resolve(false);

  if (ids.length) {
    written = update(current => {
      current.notifications = current.notifications.filter(entry => !ids.includes(entry?.jobId));
    });
  }
  return written;
};

/**
 * Registers a listener invoked with each notification as it is created, and
 * returns its disposer. This is how the panel hears about one without this
 * module knowing that a panel exists.
 */
export const onCreated = (listener: JobNotificationListener): (() => void) => (
  listeners.subscribe(listener)
);

/**
 * Clears the module. Module state outlives a test case, so this exists for test
 * isolation - never call it from application code.
 */
export const reset = (): void => {
  listeners.clear();
};
